import { PipeTransform, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { filter, tap, catchError } from 'rxjs/operators';
import { Observable, isObservable, of } from 'rxjs';

interface IAsyncPipeCache<T> {
  list?: Array<T> | Observable<Array<T>>;
  fetch?: { [key: string]: T | Observable<T>; };
}

interface IIDName {
  id: number | string;
  name: string;
}

interface IIDValue {
  id: number | string;
  value: string;
}

export interface IAlloxAsyncPipeOption {
  type?: 'html';
}

export interface IAlloxAsyncPipeList<T> {
  loadDependency(): Observable<T[]>;
  findDependency(value: number | string, source: T[]): T;
  makeText(value: number | string, obj: T, option?: {}): string;
}

export interface IAlloxAsyncPipeIDNameList<T extends IIDName> {
  loadDependency(): Observable<T[]>;
  makeText?(value: number | string, obj: T, option?: {}): string;
}

export interface IAlloxAsyncPipeIDValueList<T extends IIDValue> {
  loadDependency(): Observable<T[]>;
  makeText?(value: number | string, obj: T, option?: {}): string;
}

export interface IAlloxAsyncPipeFetch<T> {
  fetchDependency(value: number | string): Observable<T>;
  makeText(value: number | string, obj: T, option?: {}): string;
}
export abstract class AlloxCommonAsyncPipe<T> implements PipeTransform {

  public static LOADING = 'Loading...';

  private static LOADING_HTML = `
  <div class="la-ball-spin-clockwise la-dark la-sm d-inline-block mr-1">
    <div></div>
    <div></div>
    <div></div>
    <div></div>
    <div></div>
    <div></div>
    <div></div>
    <div></div>
  </div>
  <small class="align-top"> ${AlloxCommonAsyncPipe.LOADING}</small>
  `;

  private static cache: { [key: string]: IAsyncPipeCache<any>; } = {};

  private get cacheName() { return (this as Object).constructor.name; }

  constructor(private changeDetectorRef: ChangeDetectorRef, router: Router) {
    router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .pipe(filter(_ => AlloxCommonAsyncPipe.cache[(this as Object).constructor.name] != null))
      .subscribe(this.clearCache.bind(this));
  }

  transform(value: number | string, option?: 'html' | IAlloxAsyncPipeOption) {
    if (!AlloxCommonAsyncPipe.cache[this.cacheName]) {
      AlloxCommonAsyncPipe.cache[this.cacheName] = { list: null, fetch: {} };
    }

    let item: Observable<any> | T;
    const self = this as any;
    if ((self as IAlloxAsyncPipeList<T>).loadDependency) {
      item = this.findItemFromList(self, value);
    }
    if (!item && (self as IAlloxAsyncPipeFetch<T>).fetchDependency) {
      item = this.findItemFromFetch(self, value);
    }

    if (isObservable(item)) {
      return this.makeLoadingText(option || {});
    }

    if ((self as IAlloxAsyncPipeIDValueList<any>).makeText) {
      return (self as IAlloxAsyncPipeIDValueList<any>).makeText(value, item, option);
    }

    if ((self as IAlloxAsyncPipeIDNameList<any>).makeText) {
      return (self as IAlloxAsyncPipeIDNameList<any>).makeText(value, item, option);
    }
    return item ? `${item['name']}` : `Unknown [id: ${value}]`;
  }

  private findItemFromList(self: IAlloxAsyncPipeList<T>, value: number | string): Observable<any> | T {
    const cache = AlloxCommonAsyncPipe.cache[this.cacheName];
    if (!cache.list) {
      const dep = self.loadDependency();
      cache.list = dep;
      dep.pipe(
        tap(results => cache.list = results),
        catchError(_ => cache.list = [])
      )
        .subscribe(_ => this.changeDetectorRef.markForCheck());
      return dep;
    }
    if (isObservable(cache.list)) {
      return cache.list;
    }
    return cache.list.find(v => v['id'] === value);
  }

  private findItemFromFetch(self: IAlloxAsyncPipeFetch<T>, value: number | string): Observable<any> | T {
    const cache = AlloxCommonAsyncPipe.cache[this.cacheName];
    if (cache.fetch[value] === undefined) {
      const dep = self.fetchDependency(value);
      cache.fetch[value] = dep;
      dep.pipe(
        tap(result => cache.fetch[value] = result),
        catchError(err => {
          cache.fetch[value] = null;
          return of(null);
        })
      )
        .subscribe(_ => this.changeDetectorRef.markForCheck());
      return dep;
    }
    if (isObservable(cache.fetch[value])) {
      return cache.fetch[value];
    }
    return cache.fetch[value];
  }

  private makeLoadingText(option?: 'html' | IAlloxAsyncPipeOption) {
    return option === 'html' || (typeof option === 'object' && option.type === 'html') ? AlloxCommonAsyncPipe.LOADING_HTML : '';
  }

  private clearCache() {
    const name = (this as Object).constructor.name;
    delete AlloxCommonAsyncPipe.cache[name];
  }

}
